import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, User, ChevronDown, PenLine, Monitor, BookOpen, BarChart3, Zap, ShoppingCart } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import SettingsModal from './SettingsModal';
import GuideModal from '../Guide';
import PurchaseModal from './PurchaseModal';

export default function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  return (
    <>
      <header className="bg-white dark:bg-surface-950 border-b border-gray-200 dark:border-primary-800/30 px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-primary-600 rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-xs">RD</span>
            </div>
            <span className="text-sm text-gray-500 hidden sm:block">RadiologyDashboard</span>
            <span className="text-[10px] text-gray-400 dark:text-gray-600 font-mono">v1.2.0</span>
          </div>

          {/* Center: Reporting title */}
          <div className="flex items-center gap-2">
            <Monitor size={16} className="text-primary-600 dark:text-primary-400" />
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white tracking-wide">
              Reporting
            </h1>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowGuide(true)}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-surface-800 transition-colors"
              title="Guide"
            >
              <BookOpen size={18} />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-surface-800 transition-colors"
              title="Settings"
            >
              <Settings size={18} />
            </button>

            {/* Credit badge */}
            {user && user.free_reports_remaining !== undefined && (
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                  user.free_reports_remaining === null
                    ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-400'
                    : user.free_reports_remaining > 5
                      ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-400'
                      : user.free_reports_remaining > 0
                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                        : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                }`}
                title={user.free_reports_remaining === null ? 'Unlimited reports' : 'Free trial reports remaining'}
              >
                <Zap size={12} />
                {user.free_reports_remaining === null ? (
                  <>
                    <span className="hidden sm:inline">Unlimited</span>
                    <span className="sm:hidden">&infin;</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">{user.free_reports_remaining} reports left</span>
                    <span className="sm:hidden">{user.free_reports_remaining}</span>
                  </>
                )}
              </div>
            )}

            {/* Buy Reports button */}
            <button
              onClick={() => setShowPurchase(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-teal-700 dark:text-teal-400 border border-teal-300 dark:border-teal-700 rounded-md hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
              title="Buy more reports"
            >
              <ShoppingCart size={13} />
              <span className="hidden sm:inline">Buy Reports</span>
            </button>

            {/* User dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-800 rounded-md transition-colors"
              >
                <User size={16} />
                <span className="hidden sm:block text-xs">{user?.full_name || 'User'}</span>
                <ChevronDown size={14} />
              </button>

              {showUserMenu && (
                <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-surface-900 rounded-lg shadow-xl border border-gray-200 dark:border-surface-800 py-1 z-50">
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-surface-800">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user?.full_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.email}
                    </p>
                  </div>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => { setShowUserMenu(false); navigate('/admin'); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-surface-800 transition-colors"
                    >
                      <BarChart3 size={14} />
                      Admin Analytics
                    </button>
                  )}
                  <button
                    onClick={logout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut size={14} />
                    Logout
                  </button>
                </div>
              )}
            </div>

            {/* Sign Button */}
            <button className="flex items-center gap-1.5 px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md border border-primary-500/50 transition-colors">
              <PenLine size={14} />
              Sign
            </button>
          </div>
        </div>
      </header>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showGuide && <GuideModal onClose={() => setShowGuide(false)} />}
      {showPurchase && (
        <PurchaseModal
          onClose={() => setShowPurchase(false)}
          reportsRemaining={user?.free_reports_remaining}
        />
      )}
    </>
  );
}
